import { asRecord } from "../blocks/content-helpers";
import { BLOCK_KIND_REGISTRY, isBlockKindType } from "../blocks/registry";
import {
	estimateTextMs,
	FALLBACK_TOPIC_CARD_DWELL_MS,
	TOPIC_CARD_DWELL_MAX_MS,
	TOPIC_CARD_DWELL_MIN_MS,
} from "./reading-rate";
import { TOPIC_BOOKEND_MIN_MS } from "./time";

export type TopicTimelineSegmentKind = "cover" | "content" | "close";

/** Cover / close are bookend layouts; every other card is content. */
export function resolveTopicTimelineSegmentKind(
	layoutTemplate: string | null | undefined,
): TopicTimelineSegmentKind {
	if (layoutTemplate === "cover" || layoutTemplate === "close") {
		return layoutTemplate;
	}
	return "content";
}

export interface TopicDwellBlockInput {
	type: string;
	content: unknown;
	/** Playable media length when known (VIDEO). */
	knownMediaDurationMs?: number | null;
}

/**
 * A content card's dwell: the sum of each block kind's `estimateDwellMs`, clamped to
 * [TOPIC_CARD_DWELL_MIN_MS, TOPIC_CARD_DWELL_MAX_MS]. Unknown blocks → the fallback dwell.
 */
export function estimateTopicCardDwellMs(
	blocks: readonly TopicDwellBlockInput[] | null | undefined,
): number {
	if (!blocks?.length) {
		return FALLBACK_TOPIC_CARD_DWELL_MS;
	}
	let total = 0;
	for (const block of blocks) {
		const key = block.type.toUpperCase();
		if (!isBlockKindType(key)) {
			continue;
		}
		total += BLOCK_KIND_REGISTRY[key].estimateDwellMs(
			asRecord(block.content),
			{
				knownMediaDurationMs: block.knownMediaDurationMs,
			},
		);
	}
	return Math.min(
		TOPIC_CARD_DWELL_MAX_MS,
		Math.max(TOPIC_CARD_DWELL_MIN_MS, total),
	);
}

export interface ResolveTopicSegmentContentMsInput {
	kind: TopicTimelineSegmentKind;
	/** Body + summary narration length; never includes trailing silence. */
	narrationContentMs?: number | null;
	/** Content cards: the card's blocks. */
	blocks?: readonly TopicDwellBlockInput[] | null;
	/** Cover / close: the speakable bookend text. */
	bookendSpeakableText?: string | null;
}

/**
 * A segment's content length (R13), the one rule every format uses:
 * 1. narration body + summary when present;
 * 2. content cards: the card dwell from its blocks;
 * 3. cover / close: reading time of the speakable text, else {@link TOPIC_BOOKEND_MIN_MS}.
 * Trailing silence is a pause after the segment, never part of it.
 */
export function resolveTopicSegmentContentMs(
	input: ResolveTopicSegmentContentMsInput,
): number {
	const narrationMs = Math.max(0, input.narrationContentMs ?? 0);
	if (narrationMs > 0) {
		return narrationMs;
	}
	if (input.kind === "content") {
		return estimateTopicCardDwellMs(input.blocks);
	}
	const spokenMs = estimateTextMs(input.bookendSpeakableText ?? "");
	return spokenMs > 0 ? spokenMs : TOPIC_BOOKEND_MIN_MS;
}
